import { ExclamationCircleOutlined } from '@ant-design/icons';
import type {
  UploadRequestFile,
  UploadRequestOption,
} from '@rc-component/upload/es/interface';
import defaultRequest from '@rc-component/upload/es/request';
import type { UploadFile, UploadProps } from 'antd';
import { App } from 'antd';
import { ALLOWED_FILE_TYPES, MAX_UPLOAD_FILESIZE } from 'const/attachments';
import { useUploadDossierAttachmentAction } from 'hooks/dossiers.hooks';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import BaseService from 'services/api/BaseService';
import { AttachmentsUrls } from 'services/api/urls';
import { DocumentWithType } from 'types/Document';

interface OnChangeOpts {
  file: UploadFile;
  fileList: UploadFile[];
}

export const useUploadAttachmentProps = (
  attachments: UploadFile[],
  action: string,
  onFileListChanged: () => void = () => {},
): UploadProps => {
  const { t } = useTranslation();
  const currentAction = useRef(action);
  const [fileList, setFileListInternal] = useState<UploadFile[]>(attachments);
  const { notification } = App.useApp();
  const downloadAttachment = useDownloadAttachment();

  useEffect(() => {
    currentAction.current = action;
    return () => {
      currentAction.current = '';
    };
  }, [action]);

  const setFileList = useCallback(
    (newFileList: UploadFile[]) => {
      if (currentAction.current === action) {
        setFileListInternal(newFileList);
      }
    },
    [action],
  );

  useEffect(() => {
    setFileList(attachments);
  }, [attachments, setFileList]);

  const displayError = useCallback(
    (file: UploadFile, message: ReactNode) => {
      notification.open({
        icon: <ExclamationCircleOutlined />,
        message: t('attachments:upload_error'),
        description: (
          <>
            {t('attachments:upload_error_description', {
              file: file.name,
            })}
            <br />
            {message}
          </>
        ),
      });
    },
    [notification, t],
  );

  const checkAndDownloadAttachment = useCallback(
    (file: UploadFile) => {
      if (file.url && file?.linkProps?.deleting !== 'true') {
        downloadAttachment(file);
      }
    },
    [downloadAttachment],
  );

  const onChange = useCallback(
    ({ file, fileList: newFileList }: OnChangeOpts) => {
      // Réordonne la liste des fichiers pour mettre les plus récents en premier
      setFileList(
        newFileList.sort((fileA: UploadFile, fileB: UploadFile) =>
          `${fileB.lastModifiedDate}`.localeCompare(
            `${fileA.lastModifiedDate}`,
          ),
        ),
      );
      if (file.status === 'error') {
        displayError(file, file.response.message);
      } else if (file.status === 'done') {
        file.uid = file.response.id;
        file.url = '#'; // Définit un URL vide, le téléchargement étant géré de façon asynchrone
      }
    },
    [displayError, setFileList],
  );

  const beforeUpload = useCallback(
    (file: UploadFile) => {
      const errors = [];
      // Vérification du type
      if (!ALLOWED_FILE_TYPES.includes(file.type as string)) {
        errors.push(
          t('attachments:error_type', {
            type: file.type,
          }),
        );
      }

      // Vérification de la taille
      if (+(file.size || 0) > MAX_UPLOAD_FILESIZE * 1024 * 1024) {
        errors.push(
          t('attachments:error_size', {
            size: MAX_UPLOAD_FILESIZE,
          }),
        );
      }

      // Affichage et retour d'erreur
      if (errors.length) {
        displayError(
          file,
          <ul>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>,
        );
        file.status = 'error';
        return false;
      }

      return true;
    },
    [displayError, t],
  );

  const customRequest = useCallback(
    ({ onSuccess, ...args }: UploadRequestOption) => {
      const hookedOnSuccess = (
        body: any,
        fileOrXhr?: UploadRequestFile | XMLHttpRequest,
      ) => {
        onFileListChanged();
        if (onSuccess) {
          onSuccess(body, fileOrXhr);
        }
      };
      return defaultRequest({ onSuccess: hookedOnSuccess, ...args });
    },
    [onFileListChanged],
  );

  return useMemo(
    () => ({
      action,
      headers: Object.fromEntries(
        Array.from(BaseService.getHeadersAuth(true).entries()),
      ),
      customRequest,
      onChange,
      onDownload: checkAndDownloadAttachment,
      onPreview: checkAndDownloadAttachment,
      beforeUpload,
      fileList,
    }),
    [
      action,
      beforeUpload,
      checkAndDownloadAttachment,
      customRequest,
      fileList,
      onChange,
    ],
  );
};

export const useDownloadAttachment = (): any => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const displayError = (message: string, fileName: string) =>
    notification.open({
      icon: <ExclamationCircleOutlined />,
      message: t('attachments:download_error'),
      description: (
        <>
          {t('attachments:download_error_description', {
            file: fileName,
          })}
          <br />
          {message}
        </>
      ),
    });

  return async (attachment: UploadFile) => {
    try {
      const response = await BaseService.getRequest(
        AttachmentsUrls.GET_DOWNLOAD(attachment.uid),
        true,
      );
      const data = await response.json();
      if ([200, 201].includes(response.status)) {
        const link = document.createElement('a');
        link.href = AttachmentsUrls.DOWNLOAD_ATTACHMENT(data.token);
        document.body.appendChild(link);
        link.click();
        link?.parentNode?.removeChild(link);
      } else {
        displayError(data?.message, attachment.name);
      }
    } catch (e: any) {
      console.warn('Download attachment error', e);
      displayError(
        e.message || t('global:internet_connexion_error'),
        attachment.name,
      );
    }
  };
};

/**
 * Récupère l'URL de téléchargement d'une pièce jointe à partir de son identifiant.
 *
 * @param {string} attachmentId - L'ID de la pièce jointe.
 * @returns {UseQueryResult<string | null>} - Le résultat de la requête contenant l'URL de téléchargement de la pièce jointe ou `null` si non disponible.
 *
 */
export function useAttachmentUrl(attachmentId?: string) {
  return useQuery({
    queryKey: ['attachmentUrl', attachmentId],
    queryFn: async () => {
      if (attachmentId) {
        const response = await BaseService.getRequest(
          AttachmentsUrls.GET_DOWNLOAD(attachmentId),
          true,
        );
        const data = await response.json();
        if ([200, 201].includes(response.status)) {
          return AttachmentsUrls.DOWNLOAD_ATTACHMENT(data.token);
        }
      }
      return null;
    },
    enabled: !!attachmentId,
    staleTime: Infinity,
  });
}

/**
 * Upload des pièces jointes pour un dossier donné.
 * @param dossierId L'ID du dossier
 * @returns la mutation pour l'upload des pièces jointes
 */
export function useUploadAttachments(dossierId: string) {
  const { t } = useTranslation();
  const uri = useUploadDossierAttachmentAction(dossierId);

  return useMutation<string[], Error, DocumentWithType[]>({
    mutationFn: async (files) => {
      let body;
      const response = await BaseService.request(
        uri,
        'POST',
        true,
        (() => {
          const formData = new FormData();
          files?.forEach((file) => {
            if (file.originFileObj) {
              formData.append('files', file.originFileObj as Blob);
            }
          });
          formData.append(
            'typeDocuments',
            JSON.stringify(files.map((file) => file.typeDocument)),
          );
          return formData;
        })(),
        true,
      );

      try {
        body = await response.json();

        if (!response.ok) {
          throw new Error(body?.message || t('global:unknown_error'));
        }
      } catch (error) {
        throw new Error(t('global:unknown_error'));
      }

      return body as string[];
    },
  });
}
